import {
  ConvexReactClient,
  type ConvexReactClientOptions,
  type MutationOptions,
} from "convex/react";
import {
  type FunctionReference,
  type FunctionReference_future,
  getFunctionName,
} from "convex/server";
import type { Value } from "convex/values";
import { type TraceContext, withTraceContext } from "./convex-trace";

type ConvexArgs = Record<string, Value>;
type AnyReference<Kind extends "mutation" | "action"> =
  | FunctionReference<Kind>
  | FunctionReference_future<Kind>;

export interface ConvexTraceHooks {
  traceContext: () => TraceContext | undefined;
  onFailure?: (functionName: string, error: unknown) => void;
}

export class TracedConvexReactClient extends ConvexReactClient {
  private readonly traceHooks: ConvexTraceHooks;

  constructor(
    address: string,
    options: ConvexReactClientOptions | undefined,
    traceHooks: ConvexTraceHooks,
  ) {
    super(address, options);
    this.traceHooks = traceHooks;
  }

  private async reportingFailure<Result>(
    functionName: string,
    call: Promise<Result>,
  ): Promise<Result> {
    try {
      return await call;
    } catch (error) {
      this.traceHooks.onFailure?.(functionName, error);
      throw error;
    }
  }

  private traced(functionName: string, args: ConvexArgs): ConvexArgs {
    return withTraceContext(functionName, args, this.traceHooks.traceContext());
  }

  override mutation(
    mutation: AnyReference<"mutation">,
    args: ConvexArgs = {},
    options?: MutationOptions<ConvexArgs>,
  ) {
    const optimisticUpdate = options?.optimisticUpdate;
    const functionName = getFunctionName(mutation);
    return this.reportingFailure(
      functionName,
      super.mutation(mutation, this.traced(functionName, args), {
        ...options,
        ...(optimisticUpdate && {
          optimisticUpdate: (store) => optimisticUpdate(store, args),
        }),
      }),
    );
  }

  override action(action: AnyReference<"action">, args: ConvexArgs = {}) {
    const functionName = getFunctionName(action);
    return this.reportingFailure(
      functionName,
      super.action(action, this.traced(functionName, args)),
    );
  }
}
